import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme definitions
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  CYBERPUNK: 'cyberpunk',
  FLORAL: 'floral',
  ANIME: 'anime'
};

export const ThemeProvider = ({ children }) => {
  // Initialize from localStorage or default to 'light'
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('hearmeout-theme');
      return savedTheme || 'light';
    }
    return 'light';
  });

  useEffect(() => {
    // Apply theme class to document root
    const root = document.documentElement;
    
    // Remove all theme classes first
    root.classList.remove('light', 'dark', 'cyberpunk', 'floral', 'anime');
    
    // Add the current theme class
    root.classList.add(theme);
    
    // Persist to localStorage
    localStorage.setItem('hearmeout-theme', theme);
  }, [theme]);

  // Cycle through themes: light -> dark -> cyberpunk -> floral -> anime -> light
  const toggleTheme = () => {
    setTheme(prev => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'cyberpunk';
      if (prev === 'cyberpunk') return 'floral';
      if (prev === 'floral') return 'anime';
      return 'light';
    });
  };

  const setLightTheme = () => setTheme('light');
  const setDarkTheme = () => setTheme('dark');
  const setCyberpunkTheme = () => setTheme('cyberpunk');
  const setFloralTheme = () => setTheme('floral');
  const setAnimeTheme = () => setTheme('anime');

  const value = {
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    isCyberpunk: theme === 'cyberpunk',
    isFloral: theme === 'floral',
    isAnime: theme === 'anime',
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    setCyberpunkTheme,
    setFloralTheme,
    setAnimeTheme,
    setTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
